import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import api from '../../services/api';
import TextInput from '../../components/ui/TextInput.jsx';
import Button from '../../components/ui/Button';
import A11yDialog from '../../components/ui/A11yDialog';
import { useAuth } from '../../context/AuthContext';
import PoolSelectField, { POOL_ELIGIBLE_ACTIONS } from '../ats-candidate-pools/components/PoolSelectField';

// Tek başvurunun durumunu değiştiren, kendi kendine yeten diyalog: elinde bir
// başvuru nesnesi olan her yerden (detay sayfası) açılır. Liste sayfasındaki
// akışın (AtsApplicationsPage) aynası — geçiş kuralları applicationActions.js'te
// TEK KAYNAKTAN okunur, burada kural YOKTUR.
//
// BP-26 — diskalifiye gerekçesi SUNUCUDA zorunludur (400
// ats.application.disqualifyReasonRequired); düğme gerekçesiz etkinleşmez.
// FE-31 — sunucu reddederse diyalog KAPANMAZ, yazılan gerekçe korunur.
// SPEC F2 — reddet/diskalifiye aksiyonunda OPSİYONEL "Aday Havuzuna Ekle" (yalnız
// AT.Write): seçilirse gövdeye `addToPoolId` eklenir, sunucu adayı havuza da ekler.
const Modal = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: ${(p) => (p.$open ? 'flex' : 'none')};
  align-items: center;
  justify-content: center;
  z-index: 1300;
  padding: 24px;

  @media (max-width: 600px) {
    padding: 12px;
  }
`;

const ModalCard = styled.div`
  background: #fff;
  border-radius: 8px;
  max-width: 480px;
  width: 100%;
  padding: 24px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  max-height: 100%;
  overflow-y: auto;
`;

const DialogTitle = styled.h3`
  margin: 0 0 18px;
  font-size: ${({ theme }) => theme.metrics.font.size.lg};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const FieldGroup = styled.div`
  margin-bottom: 18px;
`;

const Message = styled.p`
  margin: 0 0 18px;
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const Actions = styled.div`
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  margin-top: 20px;
`;

export default function ApplicationStatusChangeDialog({
  open, application, transition, onClose, onChanged,
}) {
  const { t } = useTranslation();
  const { canWrite } = useAuth() || {};
  const writable = canWrite ? canWrite('AT') : false;
  const [reason, setReason] = useState('');
  const [poolId, setPoolId] = useState('');
  const [busy, setBusy] = useState(false);

  // Kapanışta gerekçeyi ve havuz seçimini temizle ki bayat değer bir sonraki
  // aksiyona sızmasın.
  useEffect(() => { if (!open) { setReason(''); setPoolId(''); } }, [open]);

  const requiresReason = !!(transition && transition.requiresReason);
  const actionLabel = transition ? t(transition.labelKey) : '';
  const offerPool = !!writable && !!transition && POOL_ELIGIBLE_ACTIONS.includes(transition.action);

  const handleConfirm = useCallback(async () => {
    if (!application?.id || !transition) return;
    if (requiresReason && !reason.trim()) return;
    setBusy(true);
    try {
      const body = {
        action: transition.action,
        reason: reason.trim() || undefined,
      };
      if (offerPool && poolId) body.addToPoolId = poolId;
      const res = await api.post(`/v1/ats-applications/${application.id}/status`, body);
      // services/api.js okuma/yazma yollarında reject ETMEYEBİLİR; gerçek sinyal
      // res.data.status. Başarısızlıkta diyalog açık kalır (FE-31).
      if (res?.data?.status === false) return;
      if (onChanged) onChanged();
      if (onClose) onClose();
    } catch {
      // interceptor toast'lar
    } finally {
      setBusy(false);
    }
  }, [application, transition, reason, requiresReason, offerPool, poolId, onChanged, onClose]);

  if (!application || !transition) return null;

  return (
    <A11yDialog
      open={!!open}
      onClose={onClose}
      dirty={requiresReason && !!reason.trim()}
      overlay={Modal}
      card={ModalCard}
      overlayProps={{ $open: true }}
    >
      {({ titleId }) => (
        <>
          <DialogTitle id={titleId}>
            {`${actionLabel} — ${application.candidateFullName || ''}`}
          </DialogTitle>
          {requiresReason ? (
            <FieldGroup>
              <TextInput
                label={t('ats.application.reason')}
                type="textarea"
                rows={3}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                required
              />
            </FieldGroup>
          ) : (
            <Message>{t('ats.application.statusChangeConfirm', { action: actionLabel })}</Message>
          )}
          {offerPool && (
            <FieldGroup>
              <PoolSelectField value={poolId} onChange={setPoolId} />
            </FieldGroup>
          )}
          <Actions>
            <Button variant="outline" onClick={onClose}>{t('common.cancel')}</Button>
            <Button
              data-role="ats-status-confirm"
              onClick={handleConfirm}
              disabled={busy || (requiresReason && !reason.trim())}
              loading={busy}
            >
              {t('common.confirm')}
            </Button>
          </Actions>
        </>
      )}
    </A11yDialog>
  );
}
